import { DocumentDataStore } from 'sharebase-app/dataStores/documentDataStore';
import {
	Acl,
	PermissionType,
	DocumentRevision
} from 'phoenix/domain';
import {
	ActionService,
	AuthService
} from 'phoenix/services';
import { AppAction } from 'sharebase-app/components/_app/app.actions';
import { ShareAction } from 'sharebase/components/shareDialog/shareDialog.actions';
import { DocumentAction } from 'sharebase-app/components/documentView/documentView.actions';
import { Activity } from 'sharebase/domain';
import { TrackingService } from 'sharebase/services/trackingService';
import { ShareBaseItemComments } from 'phoenix/domain/sharebase-comment';
import { ToastService } from 'sharebase/services/toastService';
import { DocumentViewService } from 'real-time/view-services/document-view.service';

require('./documentDetails.styles');
const template = require('./documentDetails.template');

export interface DocumentDetails {
	dateCreated:string;
	dateModified:string;
	fileFormat:string;
	fileSize:number;
	owner:string;
	activities:Activity[];
	comments:ShareBaseItemComments;
	versions:DocumentRevision[];
};

/**
 * @ngdoc directive
 * @name sharebase-app.directive:sbaDocumentDetails
 * @module sharebase-app
 * @restrict E
 * @description
 *
 * `sbaDocumentDetails` displays details of a document
 *
 * @usage
 *
 * <sba-document-details document-id="1234" /> 
 * 
 * @element input
 * 
 * @param {integer=} documentId The id of the document to display details for
 *  
 * @param {bool} detailsStoreResolved Has the data store for the details been resolved already
 */
export function DocumentDetailsDirective($timeout, $mdMedia, localStorageService): ng.IDirective {
	return {
		restrict: 'E',
		template,
		scope: {
			documentId: '<',
		},
		controller: DocumentDetailsController,
		controllerAs:'vm',
		bindToController:true,
		link: (scope, element, attrs, vm: DocumentDetailsController) => {
			//Set the activeDetailTab in local storage after it has changed
			scope.$watch('vm.activeDetailTab', function(newVal) {
				if (newVal === undefined || newVal === null) { return; }
				else {
					vm.localStorageService.setStoredValue('sba-doc-details-active-tab', newVal);
				}
			});
	
			//tags
			scope.$watch('vm.tagsReadonly', function(newVal) {
				if (newVal === true) { return; }
				vm.$timeout(() => {
					var input = <HTMLInputElement>element.find('md-chips').find('input')[0];
					// API expects a max length of 255
					// https://bitbucket.hylandqa.net/projects/SB/repos/phoenix-platform/browse/OnBase.Phoenix.Data.Core/Models/Main/TagType.cs#136
					input.setAttribute('maxlength', '255');
					input.focus();
					input.addEventListener('keydown', function (event) {
						if (this.value !== '') { return; }
	
						//disable hitting the delete/backspace button when there is no value in the input as this starts to delete tags
						var key = event.keyCode || event.charCode;
	
						if (key === 8 || key === 46) {
							event.preventDefault();
							return false;
						}
					});
				}, 0);
			});

			// Check if document changed, then reload details
			scope.$watch('vm.sbaDocumentDataStore.document', function(newVal){
				vm.loadDetails();
				if (vm.activeDetailTab){
					vm.loadActivities();
				}
			});
	
			scope.$on('$destroy', function() {
				vm.subs.forEach(function(sub) {
					sub();
				});
			});
		}
	}
}

DocumentDetailsDirective.$inject = [
	'$timeout',
	'$mdMedia',
	'localStorageService'
];

type SelectableVersion = DocumentRevision & {
	active:boolean;
}
export class DocumentDetailsController {
	public static $inject = [
		'$q',
		'$timeout',
		'$mdMedia',
		'$mdDialog',
		'sbaDocumentDataStore',
		'sbaTagDataStore',
		'sbToastService',
		'sbTrackingService',
		'pxClipboardService',
		'pxAuthService',
		'pxActionService',
		'sbrtsDocumentViewService',
		'localStorageService',
		'gettextCatalog'
	];

	constructor (
		private $q:ng.IQService, 
		public readonly $timeout:ng.ITimeoutService, 
		private $mdMedia, 
		private $mdDialog,
		private sbaDocumentDataStore:DocumentDataStore, 
		private sbaTagDataStore,
		private sbToastService:ToastService, 
		private sbTrackingService: TrackingService,
		private pxClipboardService,
		public pxAuthService:AuthService, 
		private pxActionService:ActionService,
		public sbrtsDocumentViewService:DocumentViewService,
		public readonly localStorageService, 
		private gettextCatalog:ng.gettext.gettextCatalog
	) {
		this.loadDetails();

		this.uiLoading = true;
		this.media = $mdMedia;
		this.tagsReadonly = true;
		this.you = gettextCatalog.getString('You');
		this.detailsStoreResolved = false;
		this.showComments = false;
		this.activeDetailTab = this.localStorageService.getStoredValue('sba-doc-details-active-tab') === '1' ? 1 : 0;
		this.loadingActivities = false;
	}

	public currentUserAcl: Acl;
	public documentId: number;
	public uiLoading;
	public media;
	public tagsReadonly: boolean;
	public you;
	public detailsStoreResolved;
	public showComments;
	public activeDetailTab: number;
	public loadingActivities;
	
	// subscriptions that need to be cleared when the component is destroyed
	public subs = [];

	public get details() {
		return this.sbaDocumentDataStore.details;
	}

	public get canEdit(): boolean {
		return this.currentUserAcl != null ? this.currentUserAcl.hasPermission(PermissionType.MODIFY) : false;
	}
	
	public get activeSecondaryView(): string {
		return this.localStorageService.getStoredValue('sba-doc-details-second-view');
	}
	public set activeSecondaryView(val: string) {
		this.localStorageService.setStoredValue('sba-doc-details-second-view', val);
	}

	private _showVersions: boolean = false;
	public get showVersions() {
		return this._showVersions;
	}
	public set showVersions(val: boolean) {
		this._showVersions = val;

		if (!this.$mdMedia('gt-sm')) { return; }
		else {
			this.localStorageService.setStoredValue('sba-doc-details-second-view', (val === true ? 'versions' : ''));
		}
	}
			
	public openComments(): void {			
		//track click
		this.sbTrackingService.trackClick('Opened item details comments','');
		this.showComments = true;
	}

	public loadDetails(): ng.IPromise<any> {
		if (!this.documentId) { return; }

		return this.sbaDocumentDataStore
			.load(this.documentId)
			.then(() => this.sbaDocumentDataStore.getDetails())
			.then(() => this.sbaDocumentDataStore.getCurrentUserAcl())
			.then((acl: Acl) => {
				this.currentUserAcl = acl;
				
				if (!this.$mdMedia('gt-sm')) {
					//remembering the user detail state on a mobile UI doesn't seem to make sense
					this.activeDetailTab = 0;
					this.showComments = false;
					this.showVersions = false;
					this.uiLoading = false;
				}

				return this.$q.all([
					this.sbaTagDataStore.loadDocumentTags(this.documentId)
				]);
			})
			.then(() => {
				this.showComments = this.activeSecondaryView === 'comments' ? true : false;
				this.showVersions = this.activeSecondaryView === 'versions' ? true : false;
				this.uiLoading = false;
			});
	}

	public loadActivities(): void {
		if (!this.documentId) { return; }
		
		let isSwitchingToActivitiesTab = this.activeDetailTab !== this.localStorageService.getStoredValue('sba-doc-details-active-tab');
		let delay =  isSwitchingToActivitiesTab ? 600 : 0; //Default transition time + 100ms to allow tab animation to complete or 0 if already viewing Tab

		this.loadingActivities = true;
		this.$timeout(() => {
			this.sbaDocumentDataStore.getActivities()
				.then(activities => this.loadingActivities = false);
		}, delay);
	}

	public queryTagSearch(text) {
		if (!this.sbaTagDataStore.userTags) {
			return this.sbaTagDataStore
				.loadMyTags()
				.then(() => this.sbaTagDataStore.userTags.filter(tag => tag.toLowerCase().indexOf(text.toLowerCase()) > -1));
		}

		return this.sbaTagDataStore.userTags.filter(tag => tag.toLowerCase().indexOf(text.toLowerCase()) > -1);
	}

	public copyHash(hashValue): void {
		try {
			this.pxClipboardService.copy(hashValue);
			this.sbToastService.showSimple(this.gettextCatalog.getString('File checksum copied.'));
		}
		catch (e) {
			this.sbToastService.showSimple(this.gettextCatalog.getString('Could not copy file checksum.'));
		}
	}

	public openShareDialog($event, targetList): void {
		this.pxActionService.emit({
			type: ShareAction.ViewShares,
			payload: targetList
		}, $event);
	}
	
	public addDocumentVersion(): void {
		this.pxActionService.emit({
			type: AppAction.Replace, 
			payload: this.sbaDocumentDataStore.document
		});
	}

	public moreVersionInfo(item:SelectableVersion) {
		// if setting the current item to active, deactivate any other items
		if (!item.active) {
			this.sbaDocumentDataStore.details.versions.forEach(v => {
				const selectable = v as SelectableVersion;
				if (selectable.active) {
					selectable.active = false;
				}
			});
		}
		item.active = !item.active;
	}

	public deleteVersion(version, event) {
		this.pxActionService.emit({
			type: AppAction.Delete, 
			payload: version
		}, event);
	}

	public downloadVersion(version) {
		this.pxActionService.emit({
			type: AppAction.Download,
			payload: version
		});
	}

	public promoteVersion(version, ev) {
		var confirm = this.$mdDialog.confirm()
			.title(this.gettextCatalog.getString('Make current version'))
			.textContent(this.gettextCatalog.getString('This will make this revision the current version.'))
			.ok(this.gettextCatalog.getString('Make Current'))
			.cancel(this.gettextCatalog.getString('Cancel'))
			.targetEvent(ev);

		this.$mdDialog.show(confirm)
			.then(() => {
				return this.sbaDocumentDataStore.promote(version);
			})
			.then(() => {
				this.pxActionService.emit({
					type: DocumentAction.RevisionPromoteSuccess
				});
				
				this.sbToastService.showSimple(this.gettextCatalog.getString('The document version has been updated'));
			})
			.catch(err => {
				if (err) {
					this.sbToastService.showSimple(this.gettextCatalog.getString('Unable to promote to current version'));
				}
			});
	}
}


// WEBPACK FOOTER //
// ./src/sharebase-app/components/documentDetails/documentDetails.ts